/**
 * Wizard Steg 5: Förhandsgranska
 *
 * Visa komplett förhandsvisning av årsredovisningen
 * Betalning krävs för export enligt CLAUDE.md
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Eye,
  CheckCircle,
  Lock,
  CreditCard,
  Loader2,
  Mail,
  RefreshCw,
  Check,
} from "lucide-react";
import { reportsApi, subscriptionsApi, authApi, type AnnualReport, type ParsedSieData, type Company, type BillingCompany } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { formatNumber, formatPercent } from "@/lib/format";
import { PaymentModal } from "@/components/PaymentModal";

interface Step5ExportProps {
  report: AnnualReport;
  company: Company;
  /** Previous step (default: 4) */
  prevStep?: number | string;
}

export function Step5Export({ report, company, prevStep = "4" }: Step5ExportProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, refreshUser, isLoading: isAuthLoading } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Activation state
  const [isActivated, setIsActivated] = useState(false);
  const [isCheckingActivation, setIsCheckingActivation] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Email verification state
  // Only consider verified if user is loaded AND emailVerified is true
  // This prevents showing "not verified" warning when user data is still loading
  const emailVerified = user ? user.emailVerified : null; // null = unknown/loading
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [isRefreshingUser, setIsRefreshingUser] = useState(true);

  // Legal consent checkboxes (mandatory before completion)
  const [legalConsents, setLegalConsents] = useState({
    reviewedCorrect: false,
    understandNoLiability: false,
    boardResponsibility: false,
  });
  const allConsentsChecked = legalConsents.reviewedCorrect && legalConsents.understandNoLiability && legalConsents.boardResponsibility;

  const sieData = report.parsedSieData as ParsedSieData | null;
  const keyFigures = sieData?.keyFigures;
  const incomeStatement = sieData?.incomeStatement;
  const balanceSheet = sieData?.balanceSheet;
  const userInput = report.userInput;
  const fiscalYear = `${report.fiscalYearStart} - ${report.fiscalYearEnd}`;

  // Disposition data from step 4
  const equity = balanceSheet?.equityAndLiabilities?.equity;
  const retainedEarnings = equity?.retainedEarnings || 0;
  const currentYearResult = equity?.currentYearResult || 0;
  const availableForDistribution = retainedEarnings + currentYearResult;
  const proposedDividend = userInput?.proposedDividend;
  const hasDisposition = proposedDividend != null;
  const carriedForward = hasDisposition ? availableForDistribution - proposedDividend : null;

  // Check activation status (company-level billing)
  const checkActivation = useCallback(async () => {
    setIsCheckingActivation(true);
    try {
      const response = await subscriptionsApi.getBilling();
      if (response.success && response.data) {
        const comp = response.data.companies.find(
          (c: BillingCompany) => c.companyId === report.companyId
        );
        setIsActivated(comp ? comp.billingStatus !== "inactive" : false);
      }
    } catch {
      // Fallback: not activated
    } finally {
      setIsCheckingActivation(false);
    }
  }, [report.companyId]);

  // Check activation on mount
  useEffect(() => {
    checkActivation();
  }, [checkActivation]);

  // Refresh user data on mount to ensure emailVerified status is up-to-date
  // This fixes a race condition where user data may be stale when navigating between wizard steps
  useEffect(() => {
    const doRefresh = async () => {
      try {
        await refreshUser();
      } finally {
        setIsRefreshingUser(false);
      }
    };
    doRefresh();
  }, []); // Only on mount

  // Handle payment success redirect from Stripe
  useEffect(() => {
    if (searchParams.get("payment") === "success") {
      const sessionId = searchParams.get("session_id");
      setPaymentSuccess(true);
      setSearchParams({}, { replace: true });

      // Verifiera checkout-sessionen direkt mot Stripe (fungerar utan webhooks)
      const verifyAndRefresh = async () => {
        if (sessionId) {
          try {
            await subscriptionsApi.verifyCheckout(sessionId);
            setIsActivated(true);
            return;
          } catch {
            // Verify misslyckades — falla tillbaka till polling
          }
        }

        // Fallback: polling om session_id saknas eller verify misslyckades
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          try {
            const response = await subscriptionsApi.getBilling();
            if (response.success && response.data) {
              const comp = response.data.companies.find(
                (c: BillingCompany) => c.companyId === report.companyId
              );
              if (comp && comp.billingStatus !== "inactive") {
                setIsActivated(true);
                clearInterval(poll);
              }
            }
          } catch {
            // ignore
          }
          if (attempts >= 15) {
            clearInterval(poll);
          }
        }, 2000);
      };

      verifyAndRefresh();
    }
  }, [searchParams, setSearchParams, report.companyId]);

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    setDownloadError(null);

    try {
      const result = await reportsApi.downloadPdf(report.id);

      if (result.success && result.blob) {
        const url = window.URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename || "arsredovisning.pdf";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success("PDF har laddats ner för granskning");
      } else {
        setDownloadError(result.error || "Kunde inte ladda ner PDF");
      }
    } catch {
      setDownloadError("Ett fel uppstod vid nedladdning");
      toast.error("Ett fel uppstod vid nedladdning");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      // Update report status to completed
      console.log("[handleComplete] Calling updateStatus with reportId:", report.id, "status:", "completed");
      const response = await reportsApi.updateStatus(report.id, "completed");
      console.log("[handleComplete] Response:", response);
      if (response.success) {
        toast.success("Årsredovisningen är nu slutförd!");
        navigate(`/dashboard/companies/${company.id}`);
      } else {
        // Handle error object or string
        console.error("[handleComplete] Error response:", response.error);
        const errorMessage = typeof response.error === "object" && response.error !== null
          ? (response.error as { message?: string }).message || "Kunde inte slutföra årsredovisningen"
          : response.error || "Kunde inte slutföra årsredovisningen";
        toast.error(errorMessage);
      }
    } catch {
      toast.error("Ett fel uppstod vid slutförande");
    } finally {
      setIsCompleting(false);
    }
  };

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!user?.email) return;

    setIsResendingVerification(true);
    setResendError(null);
    setVerificationResent(false);

    try {
      const response = await authApi.resendVerification(user.email);
      if (response.success) {
        setVerificationResent(true);
        toast.success("Verifieringsmail skickat!");
      } else {
        setResendError(response.error?.message || "Kunde inte skicka verifieringsmail");
      }
    } catch {
      setResendError("Ett fel uppstod vid sändning av verifieringsmail");
    } finally {
      setIsResendingVerification(false);
    }
  };

  // Check if email has been verified
  const handleCheckVerification = async () => {
    setIsCheckingVerification(true);
    try {
      await refreshUser();
    } finally {
      setIsCheckingVerification(false);
    }
  };

  // emailVerified is null while loading, true/false when loaded
  const canExport = isActivated && emailVerified === true;

  // Check if we're still loading user/activation state
  const isLoadingState = isAuthLoading || isRefreshingUser || isCheckingActivation;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Förhandsgranska
        </h2>
        <p className="text-gray-600">
          Granska din årsredovisning och markera den som slutförd.
        </p>
      </div>

      {/* Payment success banner */}
      {paymentSuccess && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-600" size={20} />
            <p className="font-medium text-green-800">
              {isActivated
                ? "Betalningen är genomförd! Du kan nu exportera din årsredovisning."
                : "Betalningen bearbetas. Vänta en stund..."}
            </p>
            {!isActivated && (
              <Loader2 size={16} className="animate-spin text-green-600" />
            )}
          </div>
        </div>
      )}

      {/* Activation warning banner - show at top if not activated */}
      {/* Only show when all loading states are complete to prevent flash of wrong state */}
      {!isLoadingState && !canExport && !paymentSuccess && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg max-w-2xl mx-auto">
          <div className="flex items-start space-x-3">
            <Lock className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-amber-800">
                {emailVerified === false ? "Verifiera din e-postadress" : "Aktivera för att exportera"}
              </p>
              <p className="text-sm text-amber-700 mt-1">
                {emailVerified === false
                  ? "Du måste verifiera din e-postadress innan du kan aktivera företaget och exportera årsredovisningen."
                  : "Du kan granska hela årsredovisningen gratis. För att ladda ner PDF eller skicka till Bolagsverket behöver du aktivera företaget."
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Document Teaser */}
      <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden max-w-2xl mx-auto">
        {/* Document header */}
        <div className="px-8 pt-8 pb-6 text-center border-b border-gray-100">
          <p className="text-xs font-medium tracking-widest text-gray-400 uppercase mb-3">
            Årsredovisning
          </p>
          <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
          <p className="text-sm text-gray-500 mt-1">
            Org.nr {company.organizationNumber}
          </p>
          <p className="text-sm text-gray-500">
            Räkenskapsår {fiscalYear}
          </p>
        </div>

        {/* Financial summary – two columns */}
        <div className="grid grid-cols-2 divide-x divide-gray-100">
          {/* Resultaträkning */}
          <div className="px-6 py-5">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
              Resultaträkning
            </p>
            <table className="w-full text-sm">
              <tbody>
                <tr>
                  <td className="py-1 text-gray-600">Nettoomsättning</td>
                  <td className="py-1 text-right font-medium text-gray-900">
                    {formatNumber(incomeStatement?.revenue?.netSales || 0)}
                  </td>
                </tr>
                <tr>
                  <td className="py-1 text-gray-600">Rörelseresultat</td>
                  <td className="py-1 text-right font-medium text-gray-900">
                    {formatNumber(incomeStatement?.operatingResult || 0)}
                  </td>
                </tr>
                <tr className="border-t border-gray-100">
                  <td className="pt-2 text-gray-900 font-medium">Årets resultat</td>
                  <td className={`pt-2 text-right font-semibold ${
                    (incomeStatement?.netResult || 0) >= 0 ? "text-green-600" : "text-red-600"
                  }`}>
                    {formatNumber(incomeStatement?.netResult || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Balansräkning */}
          <div className="px-6 py-5">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
              Balansräkning
            </p>
            <table className="w-full text-sm">
              <tbody>
                <tr>
                  <td className="py-1 text-gray-600">Tillgångar</td>
                  <td className="py-1 text-right font-medium text-gray-900">
                    {formatNumber(balanceSheet?.assets?.totalAssets || 0)}
                  </td>
                </tr>
                <tr>
                  <td className="py-1 text-gray-600">Eget kapital</td>
                  <td className="py-1 text-right font-medium text-gray-900">
                    {formatNumber(balanceSheet?.equityAndLiabilities?.equity?.total || 0)}
                  </td>
                </tr>
                <tr className="border-t border-gray-100">
                  <td className="pt-2 text-gray-900 font-medium">Soliditet</td>
                  <td className="pt-2 text-right font-semibold text-gray-900">
                    {formatPercent(keyFigures?.solidityPercent)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Disposition & key details */}
        {(hasDisposition || userInput?.averageEmployees != null) && (
          <div className="px-6 py-4 border-t border-gray-100 space-y-3">
            {hasDisposition && (
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                  Resultatdisposition
                </p>
                <div className="flex justify-between text-sm">
                  <div>
                    <span className="text-gray-600">Utdelning</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {formatNumber(proposedDividend)} kr
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Balanseras i ny räkning</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {formatNumber(carriedForward || 0)} kr
                    </span>
                  </div>
                </div>
              </div>
            )}
            {userInput?.averageEmployees != null && (
              <div className={`text-sm ${hasDisposition ? "pt-2 border-t border-gray-50" : ""}`}>
                <span className="text-gray-600">Medelantal anställda</span>
                <span className="ml-2 font-medium text-gray-900">
                  {userInput.averageEmployees}
                </span>
              </div>
            )}
          </div>
        )}

        {/* PDF Download section */}
        <div className="px-6 py-5 border-t border-gray-100">
          {isLoadingState ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="animate-spin text-gray-400" size={24} />
              <span className="ml-2 text-gray-500">Laddar...</span>
            </div>
          ) : emailVerified === false ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Mail className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
                <div className="flex-1">
                  <p className="font-medium text-amber-800">
                    Verifiera din e-postadress
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Du måste verifiera din e-postadress för att kunna aktivera företaget.
                    Vi har skickat ett mail till <strong>{user?.email}</strong>.
                  </p>

                  {verificationResent && (
                    <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={14} />
                      <p className="text-sm text-green-700">Nytt verifieringsmail har skickats!</p>
                    </div>
                  )}

                  {resendError && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm text-red-700">{resendError}</p>
                    </div>
                  )}

                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleCheckVerification}
                      disabled={isCheckingVerification}
                      className="btn btn-primary flex items-center justify-center"
                    >
                      {isCheckingVerification ? (
                        <Loader2 size={16} className="animate-spin mr-2" />
                      ) : (
                        <RefreshCw size={16} className="mr-2" />
                      )}
                      Jag har verifierat
                    </button>
                    <button
                      onClick={handleResendVerification}
                      disabled={isResendingVerification}
                      className="btn btn-secondary flex items-center justify-center"
                    >
                      {isResendingVerification ? (
                        <Loader2 size={16} className="animate-spin mr-2" />
                      ) : (
                        <Mail size={16} className="mr-2" />
                      )}
                      Skicka nytt mail
                    </button>
                  </div>

                  <p className="text-xs text-amber-600 mt-3">
                    Kontrollera din skräppost om du inte hittar mailet.
                  </p>
                </div>
              </div>
            </div>
          ) : canExport ? (
            <div className="space-y-3">
              {downloadError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{downloadError}</p>
                </div>
              )}

              <button
                onClick={handleDownloadPdf}
                disabled={isDownloading}
                className="btn btn-primary w-full flex items-center justify-center disabled:opacity-50"
              >
                {isDownloading ? (
                  <>
                    <Loader2 size={20} className="mr-2 animate-spin" />
                    Genererar...
                  </>
                ) : (
                  <>
                    <Eye size={20} className="mr-2" />
                    Granska årsredovisning (PDF)
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-gray-500">
                <Lock size={16} />
                <span className="text-sm">Aktivera för att ladda ner PDF</span>
              </div>
              <button
                onClick={() => setShowPaymentModal(true)}
                className="btn btn-primary flex items-center"
              >
                <CreditCard size={16} className="mr-2" />
                Aktivera företag
              </button>
            </div>
          )}
        </div>

        {/* Document sections footer */}
        <div className="px-8 py-4 bg-gray-50 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">
            Förvaltningsberättelse &middot; Resultaträkning &middot; Balansräkning &middot; Noter &middot; Underskrifter
          </p>
        </div>
      </div>

      {/* Complete section */}
      {canExport && (
        <div className={`card max-w-2xl mx-auto ${
          report.status === "finalized"
            ? "bg-primary-50 border-primary-200"
            : report.status === "completed"
              ? "bg-blue-50 border-blue-200"
              : "bg-green-50 border-green-200"
        }`}>
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${
              report.status === "finalized"
                ? "bg-primary-100"
                : report.status === "completed"
                  ? "bg-blue-100"
                  : "bg-green-100"
            }`}>
              <CheckCircle size={24} className={
                report.status === "finalized"
                  ? "text-primary-600"
                  : report.status === "completed"
                    ? "text-blue-600"
                    : "text-green-600"
              } />
            </div>
            <div className="flex-1">
              {report.status === "finalized" ? (
                <>
                  <h3 className="font-semibold text-primary-900">Skickad till Bolagsverket</h3>
                  <p className="text-sm text-primary-700 mt-1">
                    Denna årsredovisning har skickats till Bolagsverket och är permanent låst.
                    Inga ändringar kan göras.
                  </p>
                </>
              ) : report.status === "completed" ? (
                <>
                  <h3 className="font-semibold text-blue-900">Årsredovisningen är slutförd</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Du har markerat årsredovisningen som slutförd.
                  </p>
                  <button
                    onClick={() => navigate(`/dashboard/companies/${company.id}`)}
                    className="mt-4 btn bg-blue-600 hover:bg-blue-700 text-white flex items-center"
                  >
                    <ArrowLeft size={20} className="mr-2" />
                    Till företagssidan
                  </button>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-green-900">Redo att slutföra?</h3>
                  <p className="text-sm text-green-700 mt-1">
                    När du markerar årsredovisningen som slutförd kan du sedan skicka den till Bolagsverket
                    från företagssidan. Under en begränsad tid kan du göra justeringar innan du skickar in.
                  </p>
                  <p className="text-sm text-green-700 mt-2 font-medium">
                    OBS: När årsredovisningen skickas till Bolagsverket låses den permanent och kan inte längre ändras.
                  </p>

                  {/* Mandatory legal consents */}
                  <div className="mt-4 p-4 bg-white border border-green-200 rounded-lg space-y-3">
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      Bekräfta följande innan du markerar som slutförd:
                    </p>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={legalConsents.reviewedCorrect}
                        onChange={(e) => setLegalConsents(prev => ({ ...prev, reviewedCorrect: e.target.checked }))}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">
                        Jag har granskat årsredovisningen och bekräftar att uppgifterna är korrekta enligt bästa förmåga
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={legalConsents.understandNoLiability}
                        onChange={(e) => setLegalConsents(prev => ({ ...prev, understandNoLiability: e.target.checked }))}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">
                        Jag förstår att Kvanten inte ansvarar för felaktigheter som orsakas av felaktiga underlag, indata eller klassificeringar jag tillhandahållit
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={legalConsents.boardResponsibility}
                        onChange={(e) => setLegalConsents(prev => ({ ...prev, boardResponsibility: e.target.checked }))}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">
                        Jag är medveten om att det slutgiltiga ansvaret för årsredovisningens innehåll och riktighet ligger hos bolagets styrelse enligt aktiebolagslagen
                      </span>
                    </label>
                  </div>

                  <button
                    onClick={handleComplete}
                    disabled={isCompleting || !allConsentsChecked}
                    className="mt-4 btn bg-green-600 hover:bg-green-700 text-white flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCompleting ? (
                      <>
                        <Loader2 size={20} className="mr-2 animate-spin" />
                        Slutför...
                      </>
                    ) : (
                      <>
                        <Check size={20} className="mr-2" />
                        Markera som slutförd
                      </>
                    )}
                  </button>
                  {!allConsentsChecked && (
                    <p className="mt-2 text-xs text-gray-500">
                      Bocka i alla rutor ovan för att kunna slutföra
                    </p>
                  )}
                  <p className="mt-3 text-xs text-gray-500">
                    Läs mer i våra{" "}
                    <a href="/villkor" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">
                      användarvillkor
                    </a>
                    .
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          onClick={() => {
            window.scrollTo(0, 0);
            navigate(`/dashboard/wizard/${report.id}/step/${prevStep}`);
          }}
          className="btn btn-secondary flex items-center"
        >
          <ArrowLeft size={20} className="mr-2" />
          Tillbaka
        </button>

        <button
          onClick={() => navigate(`/dashboard/companies/${company.id}`)}
          className="btn btn-outline"
        >
          Spara och avsluta
        </button>
      </div>

      {/* Payment Modal (subscription mode – no fiscal year) */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        companyName={company.name}
        companyId={report.companyId}
        onActivated={() => {
          setIsActivated(true);
          setShowPaymentModal(false);
        }}
      />
    </div>
  );
}
